/**
 * Create tables and distribute all guests in one batched operation.
 * Used by the AI planner's setup_seating_plan tool and testable in isolation.
 */

import { doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  type SeatingCanvasInset,
  guestSeatingFieldPath,
  guestSeatingForPlan,
  type Guest,
  type Table,
  type TableShape,
} from "@amazing-wedding/shared-types";
import {
  canPlaceCoupleParty,
  effectiveSeatMap,
  placeCoupleInSeatMap,
  placeGuestInSeatMap,
  seatingPartySize,
} from "./seat-map";
import { findFirstEmptySeat } from "@/features/ai-planner/tools/seatingToolHelpers";
import {
  computeSpaciousTableLayout,
  DEFAULT_SEATING_ROOM_WIDTH_M,
} from "@/features/seating/table-layout";
import {
  getCanonicalCoupleIds,
  getWeddingCoupleGuests,
  isGuestShownAsCompanion,
  isWeddingCoupleGuest,
  resolveWeddingRole,
} from "@/features/guests/guest-unit-rows";
import { guestRef, planTableRef, planTablesCol } from "./firestore-paths";
import { updateSeatingPlan } from "./plan-operations";
import { VIP_TABLE_CAPACITY } from "./constants";

const BATCH_CHUNK = 400;

/** Physical seats occupied by the canonical groom + bride docs. */
export function weddingCoupleSeatingHeads(allGuests: Guest[]): number {
  return getWeddingCoupleGuests(allGuests).reduce((s, g) => s + seatingPartySize(g), 0);
}

export interface SetupSeatingPlanLabels {
  vipTable: string;
  tableName: (index: number) => string;
}

export interface SetupSeatingPlanOptions {
  capacity?: number;
  tableCount?: number;
  shape?: TableShape;
  /** Add a rectangular VIP head table for the couple (see VIP_TABLE_CAPACITY). */
  includeVipTable?: boolean;
  /** When false, only create tables — no guest assignment. Default true. */
  assignGuests?: boolean;
  /** Prefer seating guests with the same groupId at one table. Default true. */
  keepGroupsTogether?: boolean;
  /** Reserve ~1 seat per table when assigning. Default false. */
  leaveSpareSeats?: boolean;
  /** Localized default table names written to Firestore. */
  labels?: SetupSeatingPlanLabels;
  /** Clear existing assignments on this plan before distributing. Default true. */
  resetAssignments?: boolean;
  /** Current plan room inset — defaults to standard hall inset when omitted. */
  canvasInset?: SeatingCanvasInset;
  /** Current plan room width in metres — defaults to 20 m when omitted. */
  roomWidthM?: number;
}

export interface SetupSeatingPlanResult {
  tablesCreated: number;
  guestsAssigned: number;
  unassignedGuestIds: string[];
  totalHeads: number;
  /** True when the room frame was expanded to fit spaced table layout. */
  roomExpanded?: boolean;
}

type MutableTable = Table;

function ceilDiv(n: number, d: number): number {
  return Math.max(1, Math.ceil(n / d));
}

function freeSeats(
  table: MutableTable,
  byId: Record<string, Guest>,
  leaveSpareSeats = false,
): number {
  const map = effectiveSeatMap(table, byId);
  const hidden = new Set(table.hiddenSeats ?? []);
  const raw = map.filter((k, i) => k === null && !hidden.has(i)).length;
  return leaveSpareSeats ? Math.max(0, raw - 1) : raw;
}

function tableHasGroup(
  table: MutableTable,
  groupId: string | null | undefined,
  byId: Record<string, Guest>,
): boolean {
  if (!groupId) return false;
  return table.guestIds.some((gid) => byId[gid]?.groupId === groupId);
}

function clusterTotalSlots(members: Guest[]): number {
  return members.reduce((s, g) => s + seatingPartySize(g), 0);
}

function sortTableCandidates<T extends { sameGroup: boolean; free: number }>(
  ranked: T[],
  opts: {
    keepGroups: boolean;
    groupId: string | null | undefined;
    tightFitAmongSameGroup: boolean;
  },
): T[] {
  const { keepGroups, groupId, tightFitAmongSameGroup } = opts;
  return [...ranked].sort((a, b) => {
    if (keepGroups && groupId && a.sameGroup !== b.sameGroup) return a.sameGroup ? -1 : 1;
    if (keepGroups && groupId && a.sameGroup && b.sameGroup && tightFitAmongSameGroup) {
      return a.free - b.free;
    }
    return b.free - a.free;
  });
}

/** Pick the best table index for a guest party, preferring shared groups. */
export function pickTableForGuest(
  guest: Guest,
  tables: MutableTable[],
  byId: Record<string, Guest>,
  options?: { keepGroupsTogether?: boolean; leaveSpareSeats?: boolean },
): number | null {
  if (isWeddingCoupleGuest(guest)) return null;

  const partySize = seatingPartySize(guest);
  const groupId = guest.groupId;
  const keepGroups = options?.keepGroupsTogether !== false;
  const leaveSpare = options?.leaveSpareSeats ?? false;

  const ranked = sortTableCandidates(
    tables
      .map((t, index) => ({
        index,
        free: freeSeats(t, byId, leaveSpare),
        sameGroup: keepGroups && tableHasGroup(t, groupId, byId),
        isVip: t.isVip,
      }))
      .filter((c) => !c.isVip && c.free >= partySize),
    { keepGroups, groupId, tightFitAmongSameGroup: true },
  );

  return ranked[0]?.index ?? null;
}

/** Pick a table that can seat an entire guest group cluster when possible. */
export function pickTableForCluster(
  members: Guest[],
  tables: MutableTable[],
  byId: Record<string, Guest>,
  options?: { keepGroupsTogether?: boolean; leaveSpareSeats?: boolean },
): number | null {
  if (members.length === 0) return null;
  const anchor = members[0]!;
  if (isWeddingCoupleGuest(anchor)) return null;

  const groupId = anchor.groupId;
  const keepGroups = options?.keepGroupsTogether !== false;
  const leaveSpare = options?.leaveSpareSeats ?? false;
  const totalSlots = clusterTotalSlots(members);

  const candidates = tables
    .map((t, index) => ({
      index,
      free: freeSeats(t, byId, leaveSpare),
      sameGroup: keepGroups && tableHasGroup(t, groupId, byId),
      isVip: t.isVip,
      fitsCluster: freeSeats(t, byId, leaveSpare) >= totalSlots,
      isEmpty: t.guestIds.length === 0,
    }))
    .filter((c) => !c.isVip && c.fitsCluster);

  if (candidates.length === 0) {
    return pickTableForGuest(anchor, tables, byId, options);
  }

  const ranked = sortTableCandidates(candidates, {
    keepGroups,
    groupId,
    tightFitAmongSameGroup: false,
  }).sort((a, b) => {
    if (a.isEmpty !== b.isEmpty) return a.isEmpty ? -1 : 1;
    return a.free - b.free;
  });

  return ranked[0]?.index ?? null;
}

/** Guest clusters by groupId (largest parties first within each cluster). */
export function groupClustersForDistribution(guests: Guest[]): Guest[][] {
  const byGroup = new Map<string | null, Guest[]>();
  for (const g of guests) {
    const key = g.groupId ?? null;
    const list = byGroup.get(key) ?? [];
    list.push(g);
    byGroup.set(key, list);
  }

  const sortMembers = (members: Guest[]) =>
    [...members].sort((a, b) => {
      const aHasPlusOne = a.plusOne != null ? 1 : 0;
      const bHasPlusOne = b.plusOne != null ? 1 : 0;
      if (aHasPlusOne !== bHasPlusOne) return bHasPlusOne - aHasPlusOne;
      return seatingPartySize(b) - seatingPartySize(a);
    });

  const clusters = [...byGroup.entries()].map(([, members]) => ({
    members: sortMembers(members),
    totalSlots: members.reduce((s, g) => s + seatingPartySize(g), 0),
  }));

  clusters.sort((a, b) => b.totalSlots - a.totalSlots);
  return clusters.map((c) => c.members);
}

/** Order guests: paired (plus-one slot) first, then larger parties; keep groupId clusters adjacent. */
export function orderGuestsForDistribution(guests: Guest[]): Guest[] {
  return groupClustersForDistribution(guests).flat();
}

function assignCoupleInMemory(
  groom: Guest,
  bride: Guest,
  table: MutableTable,
  byId: Record<string, Guest>,
  leaveSpareSeats = false,
): boolean {
  const seatMap = effectiveSeatMap(table, byId);
  const hidden = table.hiddenSeats ?? [];
  const partySize = seatingPartySize(groom) + seatingPartySize(bride);
  if (leaveSpareSeats && freeSeats(table, byId, false) < partySize + 1) return false;

  for (let seatIndex = 0; seatIndex < table.capacity; seatIndex++) {
    if (!canPlaceCoupleParty(seatMap, groom, bride, seatIndex, hidden, table.shape)) continue;
    const placed = placeCoupleInSeatMap(seatMap, groom, bride, seatIndex, hidden, table.shape);
    if (!placed) continue;
    table.seatMap = placed;
    for (const id of [groom.id, bride.id]) {
      if (!table.guestIds.includes(id)) {
        table.guestIds = [...table.guestIds, id];
      }
    }
    return true;
  }
  return false;
}

function assignGuestInMemory(
  guest: Guest,
  table: MutableTable,
  byId: Record<string, Guest>,
  leaveSpareSeats = false,
): boolean {
  const seatMap = effectiveSeatMap(table, byId);
  const partySize = seatingPartySize(guest);
  if (leaveSpareSeats && freeSeats(table, byId, false) < partySize + 1) return false;
  const seatIndex = findFirstEmptySeat(seatMap, guest, table.hiddenSeats ?? []);
  if (seatIndex == null) return false;

  table.seatMap = placeGuestInSeatMap(seatMap, guest, seatIndex, table.hiddenSeats ?? [], {
    tableShape: table.shape,
  });
  if (!table.guestIds.includes(guest.id)) {
    table.guestIds = [...table.guestIds, guest.id];
  }
  return true;
}

async function commitInChunks(
  ops: Array<(batch: ReturnType<typeof writeBatch>) => void>,
): Promise<void> {
  for (let i = 0; i < ops.length; i += BATCH_CHUNK) {
    const batch = writeBatch(db);
    ops.slice(i, i + BATCH_CHUNK).forEach((op) => op(batch));
    await batch.commit();
  }
}

/**
 * Create round tables (optional VIP head table) and seat every guest on the list.
 * Ignores RSVP — all guests count toward capacity planning.
 */
export async function setupSeatingPlan(
  weddingId: string,
  planId: string,
  guests: Guest[],
  legacyPlanId: string | null | undefined,
  options: SetupSeatingPlanOptions = {},
): Promise<SetupSeatingPlanResult> {
  const capacity = Math.min(30, Math.max(2, Math.round(options.capacity ?? 8)));
  const shape: TableShape = options.shape ?? "round";
  const includeVip = options.includeVipTable !== false;
  const assignGuests = options.assignGuests !== false;
  const keepGroupsTogether = options.keepGroupsTogether !== false;
  const leaveSpareSeats = options.leaveSpareSeats ?? false;
  const resetAssignments = options.resetAssignments !== false;
  const labels = options.labels ?? {
    vipTable: "Stół VIP",
    tableName: (index: number) => `Stół ${index}`,
  };

  const coupleIds = getCanonicalCoupleIds(guests);

  const toSeatAll = guests.filter((g) => {
    if (isGuestShownAsCompanion(g, guests)) return false;
    if (resetAssignments) return true;
    return !guestSeatingForPlan(g, planId, legacyPlanId).tableId;
  });
  const toSeatGuests = toSeatAll.filter((g) => !coupleIds.has(g.id));
  const coupleToSeat = getWeddingCoupleGuests(guests).filter((g) =>
    toSeatAll.some((x) => x.id === g.id),
  );

  const totalHeads = toSeatAll.reduce((s, g) => s + seatingPartySize(g), 0);
  const guestHeads = toSeatGuests.reduce((s, g) => s + seatingPartySize(g), 0);
  const tableCount = options.tableCount ?? ceilDiv(guestHeads > 0 ? guestHeads : 8, capacity);

  if (tableCount <= 0 && !includeVip) {
    return { tablesCreated: 0, guestsAssigned: 0, unassignedGuestIds: [], totalHeads };
  }

  if (assignGuests && toSeatAll.length === 0) {
    return { tablesCreated: 0, guestsAssigned: 0, unassignedGuestIds: [], totalHeads: 0 };
  }

  const clearOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
  if (assignGuests && resetAssignments) {
    for (const g of guests) {
      const seated = guestSeatingForPlan(g, planId, legacyPlanId).tableId;
      if (!seated) continue;
      clearOps.push((batch) =>
        batch.update(guestRef(weddingId, g.id), {
          [guestSeatingFieldPath(planId)]: { tableId: null, seatNumber: null },
          updatedAt: serverTimestamp(),
        }),
      );
    }
  }
  if (clearOps.length > 0) await commitInChunks(clearOps);

  const tables: MutableTable[] = [];
  const now = new Date().toISOString();
  let tableIndex = 0;

  const totalTables = (includeVip ? 1 : 0) + tableCount;
  const { positions: layoutPositions, planLayout } = computeSpaciousTableLayout(totalTables, {
    maxCapacity: capacity,
    shape,
    includeVipTable: includeVip,
    canvasInset: options.canvasInset,
    roomWidthM: options.roomWidthM ?? DEFAULT_SEATING_ROOM_WIDTH_M,
  });

  if (planLayout) {
    await updateSeatingPlan(weddingId, planId, {
      seatingCanvasInset: planLayout.seatingCanvasInset,
      seatingRoomWidthM: planLayout.seatingRoomWidthM,
    });
  }

  const createOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];

  if (includeVip) {
    const ref = doc(planTablesCol(weddingId, planId));
    const pos = layoutPositions[tableIndex++]!;
    const vipTable: MutableTable = {
      id: ref.id,
      name: labels.vipTable,
      shape: "rectangular",
      capacity: VIP_TABLE_CAPACITY,
      positionX: pos.positionX,
      positionY: pos.positionY,
      rotation: 0,
      guestIds: [],
      seatMap: [],
      hiddenSeats: [],
      isVip: true,
      color: null,
      notes: null,
      createdAt: now,
      updatedAt: now,
    };
    tables.push(vipTable);
    createOps.push((batch) =>
      batch.set(ref, {
        name: vipTable.name,
        shape: vipTable.shape,
        capacity: vipTable.capacity,
        positionX: vipTable.positionX,
        positionY: vipTable.positionY,
        rotation: 0,
        guestIds: [],
        seatMap: [],
        hiddenSeats: [],
        isVip: true,
        color: null,
        notes: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    );
  }

  for (let i = 0; i < tableCount; i++) {
    const ref = doc(planTablesCol(weddingId, planId));
    const pos = layoutPositions[tableIndex++]!;
    const t: MutableTable = {
      id: ref.id,
      name: labels.tableName(i + 1),
      shape,
      capacity,
      positionX: pos.positionX,
      positionY: pos.positionY,
      rotation: 0,
      guestIds: [],
      seatMap: [],
      hiddenSeats: [],
      isVip: false,
      color: null,
      notes: null,
      createdAt: now,
      updatedAt: now,
    };
    tables.push(t);
    createOps.push((batch) =>
      batch.set(ref, {
        name: t.name,
        shape: t.shape,
        capacity: t.capacity,
        positionX: t.positionX,
        positionY: t.positionY,
        rotation: 0,
        guestIds: [],
        seatMap: [],
        hiddenSeats: [],
        isVip: false,
        color: null,
        notes: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    );
  }

  await commitInChunks(createOps);

  if (!assignGuests) {
    return {
      tablesCreated: tables.length,
      guestsAssigned: 0,
      unassignedGuestIds: toSeatAll.map((g) => g.id),
      totalHeads,
      roomExpanded: planLayout != null,
    };
  }

  const byId = Object.fromEntries(guests.map((g) => [g.id, g]));
  const clusters = groupClustersForDistribution(toSeatGuests);
  const unassigned: string[] = [];
  let assigned = 0;
  const pickOpts = { keepGroupsTogether, leaveSpareSeats };
  const maxGuestTableCapacity = Math.max(
    0,
    ...tables.filter((t) => !t.isVip).map((t) => t.capacity),
  );

  const vipTable = tables.find((t) => t.isVip);
  if (vipTable && coupleToSeat.length >= 2) {
    const groom = coupleToSeat.find((g) => resolveWeddingRole(g) === "groom") ?? coupleToSeat[0]!;
    const bride = coupleToSeat.find((g) => resolveWeddingRole(g) === "bride") ?? coupleToSeat[1]!;
    if (assignCoupleInMemory(groom, bride, vipTable, byId, leaveSpareSeats)) {
      assigned += 2;
    } else {
      unassigned.push(groom.id, bride.id);
    }
  } else if (vipTable && coupleToSeat.length === 1) {
    unassigned.push(coupleToSeat[0]!.id);
  }

  const seatGuest = (guest: Guest) => {
    const tableIdx = pickTableForGuest(guest, tables, byId, pickOpts);
    if (tableIdx == null) {
      unassigned.push(guest.id);
      return;
    }
    const table = tables[tableIdx]!;
    if (assignGuestInMemory(guest, table, byId, leaveSpareSeats)) {
      assigned += 1;
    } else {
      unassigned.push(guest.id);
    }
  };

  for (const cluster of clusters) {
    const groupId = cluster[0]?.groupId;
    const totalSlots = clusterTotalSlots(cluster);
    const canBatch =
      keepGroupsTogether &&
      groupId != null &&
      cluster.length > 1 &&
      totalSlots <= maxGuestTableCapacity;

    if (canBatch) {
      const tableIdx = pickTableForCluster(cluster, tables, byId, pickOpts);
      if (tableIdx != null) {
        const table = tables[tableIdx]!;
        const snapshot = {
          guestIds: [...table.guestIds],
          seatMap: [...table.seatMap],
        };
        let batchOk = true;
        for (const guest of cluster) {
          if (!assignGuestInMemory(guest, table, byId, leaveSpareSeats)) {
            batchOk = false;
            break;
          }
        }
        if (batchOk) continue;
        table.guestIds = snapshot.guestIds;
        table.seatMap = snapshot.seatMap;
      }
    }

    for (const guest of cluster) {
      seatGuest(guest);
    }
  }

  const updateOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];

  for (const table of tables) {
    if (table.guestIds.length === 0) continue;
    updateOps.push((batch) =>
      batch.update(planTableRef(weddingId, planId, table.id), {
        guestIds: table.guestIds,
        seatMap: table.seatMap,
        updatedAt: serverTimestamp(),
      }),
    );
    for (const gid of table.guestIds) {
      const hostIdx = table.seatMap.findIndex((k) => k === gid);
      updateOps.push((batch) =>
        batch.update(guestRef(weddingId, gid), {
          [guestSeatingFieldPath(planId)]: {
            tableId: table.id,
            seatNumber: hostIdx >= 0 ? hostIdx : null,
          },
          updatedAt: serverTimestamp(),
        }),
      );
    }
  }

  if (updateOps.length > 0) await commitInChunks(updateOps);

  return {
    tablesCreated: tables.length,
    guestsAssigned: assigned,
    unassignedGuestIds: unassigned,
    totalHeads,
    roomExpanded: planLayout != null,
  };
}
